// prism/frontend/src/api/protocols.js
// Protocol management functions

import { API_BASE, getAuthHeaders, getCacheKey, fetchWithDedup, setInCache, CACHE_CONFIG, invalidateCache } from './client.js';
import { logout } from './auth.js';

// Get protocols with caching
export async function getProtocols(forceRefresh = false) {
  try {
    const cacheKey = getCacheKey("protocols");
    
    // Clear cache if force refresh requested
    if (forceRefresh) {
      invalidateCache(cacheKey);
    }
    
    return fetchWithDedup(
      cacheKey,
      async () => {
        const res = await fetch(`${API_BASE}/api/protocols`, {
          headers: getAuthHeaders(),
        });
        if (res.status === 401) {
          logout();
          throw new Error("Unauthorized - please login again");
        }
        if (!res.ok) throw new Error("Failed to fetch protocols");
        return res.json();
      },
      CACHE_CONFIG.protocols
    );
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// Force refresh protocols (re-cluster) - always bypasses cache
export async function refreshProtocols() {
  try {
    // Clear cache
    invalidateCache(getCacheKey("protocols"));
    
    const res = await fetch(`${API_BASE}/api/protocols/refresh`, {
      method: "POST",
      headers: getAuthHeaders(),
    });
    if (res.status === 401) {
      logout();
      throw new Error("Unauthorized - please login again");
    }
    if (!res.ok) throw new Error("Failed to refresh protocols");
    const data = await res.json();
    
    // Update cache with fresh data
    setInCache(getCacheKey("protocols"), data, CACHE_CONFIG.protocols);
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// Regenerate summary for a specific protocol
export async function regenerateProtocolSummary(clusterHash) {
  try {
    // Clear protocol cache since data changed
    invalidateCache(getCacheKey("protocols"));
    
    const res = await fetch(`${API_BASE}/api/protocols/${clusterHash}/regenerate`, {
      method: "POST",
      headers: getAuthHeaders(),
    });
    if (res.status === 401) {
      logout();
      throw new Error("Unauthorized - please login again");
    }
    if (!res.ok) throw new Error("Failed to regenerate summary");
    return res.json();
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// Delete protocol
export async function deleteProtocol(protocol) {
  try {
    // Clear protocol cache since data changed
    invalidateCache(getCacheKey("protocols"));
    
    const clusterHash = protocol.cluster_hash;
    if (!clusterHash) {
      throw new Error("Protocol missing cluster_hash");
    }
    
    const res = await fetch(`${API_BASE}/api/protocols/${clusterHash}`, {
      method: "DELETE",
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ vector_ids: [] }), // Protocols don't have vector_ids, send empty array
    });
    if (res.status === 401) {
      logout();
      throw new Error("Unauthorized - please login again");
    }
    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.detail || error.message || "Failed to delete protocol");
    }
    return res.json();
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// Regenerate all protocols
export async function regenerateAllProtocols() {
  try {
    // Clear all caches to force fresh fetch
    invalidateCache('all');
    
    const res = await fetch(`${API_BASE}/api/protocols/clear-summaries`, {
      method: "POST",
      headers: getAuthHeaders(),
    });
    if (res.status === 401) {
      logout();
      throw new Error("Unauthorized - please login again");
    }
    if (!res.ok) throw new Error("Failed to regenerate protocols");
    return res.json();
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// Approve protocol
export async function approveProtocol(clusterHash) {
  try {
    // Clear protocol cache
    invalidateCache(getCacheKey("protocols"));
    
    const res = await fetch(`${API_BASE}/api/protocols/${clusterHash}/approve`, {
      method: "POST",
      headers: getAuthHeaders(),
    });
    if (res.status === 401) {
      logout();
      throw new Error("Unauthorized - please login again");
    }
    if (!res.ok) throw new Error("Failed to approve protocol");
    return res.json();
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// Unapprove protocol
export async function unapproveProtocol(clusterHash) {
  try {
    // Clear protocol cache
    invalidateCache(getCacheKey("protocols"));
    
    const res = await fetch(`${API_BASE}/api/protocols/${clusterHash}/approve`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });
    if (res.status === 401) {
      logout();
      throw new Error("Unauthorized - please login again");
    }
    if (!res.ok) throw new Error("Failed to unapprove protocol");
    return res.json();
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// Update protocol summary
export async function updateProtocolSummary(clusterHash, summary) {
  try {
    // Clear protocol cache
    invalidateCache(getCacheKey("protocols"));
    
    const res = await fetch(`${API_BASE}/api/protocols/${clusterHash}/summary`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify({ summary }),
    });
    if (res.status === 401) {
      logout();
      throw new Error("Unauthorized - please login again");
    }
    if (!res.ok) {
      let errorMsg = `Failed to update protocol summary (${res.status})`;
      try {
        const errorData = await res.json();
        if (errorData.detail) errorMsg += `: ${errorData.detail}`;
      } catch (e) {
        // Couldn't parse error response
      }
      throw new Error(errorMsg);
    }
    return res.json();
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// Migrate protocol edits from localStorage to database (one summary update per edit)
export async function migrateProtocolEdits(edits) {
  const results = [];
  for (const [clusterHash, summary] of Object.entries(edits)) {
    results.push(await updateProtocolSummary(clusterHash, summary));
  }
  return { migrated: results.length };
}

// Deny protocol (removes it from protocol list)
export async function denyProtocol(clusterHash, feedback = { reason: "not_useful", details: "" }) {
  try {
    // Clear protocol cache
    invalidateCache(getCacheKey("protocols"));
    
    const res = await fetch(`${API_BASE}/api/protocols/${clusterHash}/deny`, {
      method: "POST",
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(feedback),
    });
    if (res.status === 401) {
      logout();
      throw new Error("Unauthorized - please login again");
    }
    if (!res.ok) throw new Error("Failed to deny protocol");
    return res.json();
  } catch (error) {
    console.error(error);
    throw error;
  }
}
